import { format, isToday, parseISO } from 'date-fns'

import {
  DATE_API_DATA_FORMAT_SHORT,
  DATE_FULL_FORMAT_FROM_TIME,
  DATE_HOURS_FORMAT,
  DATE_LONG_FORMAT,
} from '../../../constants/date'
import { toUkTimezoneDate } from '../../../utils/date'
import { isWorkerUser } from '../../../utils/user'

import {
  ENTITY_PROFILE_TAB_UPDATES_INITIAL_VISIBLE_COUNT,
  ENTITY_PROFILE_TAB_UPDATES_OWNER_TYPE,
} from './EntityProfileTabUpdates.constants'

function getEntityProfileTabUpdateDateKey(timestamp, timeZone) {
  return format(
    toUkTimezoneDate(parseISO(timestamp), timeZone),
    DATE_API_DATA_FORMAT_SHORT
  )
}

// Maps a log-event `initiated_by` (backend NestedProfileSerializer) to the
// `owner_obj` shape the updates card reads.
export function getEntityProfileTabUpdateOwner(initiatedBy) {
  if (!initiatedBy) {
    return null
  }

  return {
    avatar: initiatedBy.avatar,
    avatar_color: initiatedBy.avatar_color,
    deleted: initiatedBy.deleted,
    fullname: [initiatedBy.name, initiatedBy.second_name || initiatedBy.surname]
      .filter(Boolean)
      .join(' '),
    group: initiatedBy.role,
    id: initiatedBy.id,
    is_super_admin: initiatedBy.is_super_admin,
    is_system: initiatedBy.is_system,
  }
}

// Super admin is the iDWELL support account — customers must not see it as a manager.
export function getEntityProfileTabUpdateOwnerType(owner) {
  if (owner?.is_system || owner?.is_super_admin) {
    return ENTITY_PROFILE_TAB_UPDATES_OWNER_TYPE.system
  }

  if (!owner || owner.deleted) {
    return ENTITY_PROFILE_TAB_UPDATES_OWNER_TYPE.deleted
  }

  if (isWorkerUser({ role: owner.group })) {
    return ENTITY_PROFILE_TAB_UPDATES_OWNER_TYPE.worker
  }

  return ENTITY_PROFILE_TAB_UPDATES_OWNER_TYPE.regular
}

export function isEntityProfileTabUpdateOwnerSystem(type) {
  return type === ENTITY_PROFILE_TAB_UPDATES_OWNER_TYPE.system
}

export function isEntityProfileTabUpdateOwnerDeleted(type) {
  return type === ENTITY_PROFILE_TAB_UPDATES_OWNER_TYPE.deleted
}

export function getEntityProfileTabUpdatesSortedNewestFirst(items) {
  return items.slice().sort((a, b) => {
    if (a.timestamp === b.timestamp) {
      return 0
    }

    return a.timestamp < b.timestamp ? 1 : -1
  })
}

export function groupEntityProfileTabUpdatesByDate(items, timeZone) {
  return items.reduce((groups, item, index) => {
    const dateKey = getEntityProfileTabUpdateDateKey(item.timestamp, timeZone)
    const lastGroup = groups[groups.length - 1]

    if (lastGroup?.dateKey === dateKey) {
      lastGroup.items.push(item)
    } else {
      groups.push({
        dateKey,
        items: [item],
        startIndex: index,
        timestamp: item.timestamp,
      })
    }

    return groups
  }, [])
}

export function formatEntityProfileTabUpdateDateHeading({
  locale,
  t,
  timestamp,
  timeZone,
}) {
  const ukDate = toUkTimezoneDate(parseISO(timestamp), timeZone)

  if (isToday(ukDate)) {
    return t('Common.TodayDateKey')
  }

  return format(ukDate, DATE_LONG_FORMAT, {
    locale,
  })
}

export function formatEntityProfileTabUpdateCardTimestamp({
  locale,
  timestamp,
  timeZone,
}) {
  const ukDate = toUkTimezoneDate(parseISO(timestamp), timeZone)

  return format(
    ukDate,
    isToday(ukDate) ? DATE_HOURS_FORMAT : DATE_FULL_FORMAT_FROM_TIME,
    { locale }
  )
}

export function getEntityProfileTabUpdatesShowAllButtonText({ t, totalCount }) {
  const hiddenCount =
    totalCount - ENTITY_PROFILE_TAB_UPDATES_INITIAL_VISIBLE_COUNT

  return `${t('Common.ListShowAllBtnKey')} (${hiddenCount})`
}
